import { useState } from 'react';

import { debounce } from 'lodash';

import {
  Autocomplete,
  Box,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  TextField,
} from '@mui/material';

import CompanyAvatar from './CompanyAvatar';
import Button from 'components/Button';
import Text from 'components/Text';
import { apiOrchestrator } from 'services/apiService';

interface Company {
  uuid: string;
  name: string;
  email: string;
  organization_uuid: string;
  created_at: string;
  updated_at: string;
}

interface CompanyAutocompleteProps {
  onCompanySelect: (company: Company | null) => void;
  onClearSelection: () => void;
  selectedCompany: Company | null;
  onAddNewCompany: () => void;
}

const getCompanyLogoUrl = (uuid: string) => `/api/companies/${uuid}/image`;

const CompanyAutocomplete = ({
  onCompanySelect,
  onClearSelection,
  selectedCompany,
  onAddNewCompany,
}: CompanyAutocompleteProps) => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [inputValue, setInputValue] = useState('');

  const searchCompanies = async (name: string) => {
    if (!name.trim()) {
      setCompanies([]);
      return;
    }

    try {
      const response = await apiOrchestrator.get(`/companies/?name=${name}`);
      setCompanies(response.data);
    } catch (error) {
      console.error('Erro ao buscar empresas:', error);
      setCompanies([]);
    }
  };

  const debouncedSearch = debounce(searchCompanies, 500);

  const showDetails = !!selectedCompany;

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        alignItems: 'flex-start',
        flexDirection: 'column',
        width: '100%',
      }}
    >
      <Text color="textPrimary" bold>
        {selectedCompany ? 'Empresa selecionada' : 'Buscar por nome da empresa'}
      </Text>
      {!showDetails && (
        <Autocomplete
          sx={{ width: '100%' }}
          options={companies}
          getOptionLabel={option => option.name}
          inputValue={inputValue}
          onInputChange={(_, newValue) => {
            setInputValue(newValue);
            debouncedSearch(newValue);
          }}
          onChange={(_, newValue) => {
            if (newValue) {
              onCompanySelect(newValue);
            }
          }}
          noOptionsText="Nenhuma empresa encontrada com esse nome."
          renderOption={(props, option) => (
            <ListItem {...props} key={option.uuid}>
              <ListItemAvatar>
                <CompanyAvatar companyUuid={option.uuid || ''} alt={option.name || ''} size={24} />
              </ListItemAvatar>
              <ListItemText
                primary={option.name}
                secondary={`(${option.email})`}
                sx={{ display: 'flex', alignItems: 'end', gap: 1 }}
              />
            </ListItem>
          )}
          renderInput={params => (
            <TextField
              {...params}
              label="Buscar empresa"
              variant="outlined"
              fullWidth
              helperText="Digite para buscar uma empresa existente"
            />
          )}
          ListboxProps={{
            style: {
              maxHeight: '24rem',
              overflowY: 'auto',
            },
          }}
          PaperComponent={({ children, ...paperProps }) => (
            <Paper {...paperProps} sx={{ position: 'relative' }}>
              {children}
              <Box
                sx={{
                  position: 'sticky',
                  bottom: 0,
                  left: 0,
                  width: '100%',
                  zIndex: 1,
                  textAlign: 'center',
                }}
              >
                <Button
                  fullWidth
                  variant="minimal"
                  onMouseDown={(event: React.MouseEvent) => {
                    event.preventDefault();
                    onCompanySelect(null);
                    onAddNewCompany();
                  }}
                >
                  + Adicionar nova empresa
                </Button>
              </Box>
            </Paper>
          )}
          disabled={!!selectedCompany}
        />
      )}
      {showDetails && (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', width: '100%' }}>
          {selectedCompany ? (
            <>
              <CompanyAvatar
                companyUuid={selectedCompany.uuid || ''}
                alt={selectedCompany.name || ''}
                size={40}
              />
              <Box>
                <Text size="xLarge" color="textPrimary" bold>
                  {selectedCompany.name}
                </Text>
              </Box>
              <Button
                size="small"
                variant="minimal"
                onClick={() => {
                  onClearSelection();
                }}
              >
                Limpar seleção
              </Button>
            </>
          ) : (
            <>
              <Text size="large" color="textPrimary" bold>
                Nova empresa
              </Text>
              <Button size="small" variant="minimal" onClick={onAddNewCompany}>
                Cancelar
              </Button>
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

export default CompanyAutocomplete;
